import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, LogOut, ChevronRight } from 'lucide-react';
import { AppState } from '../../types';
import { GroupId, StartingPeriod } from '../../config/systemConfig';
import { Card } from '../ui/Card';
import { ProfileSection } from './ProfileSection';
interface SettingsPageProps {
  state: AppState;
  selectedGroup: GroupId;
  startingPeriod: StartingPeriod; // Effective period
  manualPeriod?: StartingPeriod | null; // Manual override state
  onSetStartingPeriod: (period: StartingPeriod | null) => void;
}
type SettingsTab = 'profile';
const tabs: {
  id: SettingsTab;
  label: string;
  icon: React.ElementType;
}[] = [{
  id: 'profile',
  label: 'Profiel',
  icon: User
}];
export function SettingsPage({
  state,
  selectedGroup,
  startingPeriod,
  manualPeriod,
  onSetStartingPeriod
}: SettingsPageProps) {
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');
  return <div className="max-w-5xl mx-auto" data-id="element-1645">
      {/* Header */}
      <div className="mb-8" data-id="element-1646">
        <h1 className="text-2xl font-bold text-slate-900" data-id="element-1647">Instellingen</h1>
        <p className="text-slate-500 mt-1" data-id="element-1648">Beheer je profiel en instellingen</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-1649">
        {/* Sidebar Navigation */}
        <div className="lg:col-span-3" data-id="element-1650">
          <Card className="p-2 sticky top-8" data-id="element-1651">
            <nav className="space-y-1" data-id="element-1652">
              {tabs.map(tab => {
              const isActive = activeTab === tab.id;
              return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`
                      w-full flex items-center justify-between p-3 rounded-xl transition-all duration-200 relative
                      ${isActive ? 'bg-indigo-50 text-indigo-700 font-bold' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 font-medium'}
                    `} data-id="element-1653">
                    <div className="flex items-center gap-3" data-id="element-1654">
                      <tab.icon className={`w-5 h-5 ${isActive ? 'text-indigo-600' : 'text-slate-400'}`} data-id="element-1655" />
                      {tab.label}
                    </div>
                    {isActive && <motion.div layoutId="settings-tab-indicator" data-id="element-1656">
                        <ChevronRight className="w-4 h-4 text-indigo-400" data-id="element-1657" />
                      </motion.div>}
                  </button>;
            })}
            </nav>

            <div className="mt-4 pt-4 border-t border-slate-100 px-2" data-id="element-1658">
              <button className="w-full flex items-center gap-3 p-3 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-colors text-sm font-medium" data-id="element-1659">
                <LogOut className="w-4 h-4" data-id="element-1660" />
                Uitloggen
              </button>
            </div>
          </Card>
        </div>

        {/* Main Content Area */}
        <div className="lg:col-span-9" data-id="element-1661">
          <AnimatePresence mode="wait" data-id="element-1662">
            <motion.div key={activeTab} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} transition={{
            duration: 0.2,
            ease: 'easeOut'
          }} data-id="element-1663">
              {activeTab === 'profile' && <ProfileSection stats={state.stats} selectedGroup={selectedGroup} startingPeriod={startingPeriod} manualPeriod={manualPeriod} onSetStartingPeriod={onSetStartingPeriod} data-id="element-1664" />}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>;
}